import React, { useState } from "react";
import { Button, Checkbox, Field, HStack, IconButton, Input, Popover, Portal, Separator, Spinner, Text } from "@chakra-ui/react";
import { LuSendHorizontal, LuTable, LuTextSearch } from "react-icons/lu";
import { getBoundingBox, showToast } from "../../../utilies/Utility";
import { useAppStore } from "../../../context/store";
import Type from "../../../utilies/Types";
import { Tooltip } from "../../../components/ui/tooltip";

function QueryMenu() {
  // state
  const qeuryMenuTable = useAppStore((s) => s.qeuryMenuTable);
  const showQeuryMenu = useAppStore((s) => s.showQeuryMenu);

  // setters / refs
  const viewerRef = useAppStore.getState().viewerRef;
  const setQeuryMenuTable = useAppStore.getState().setQeuryMenuTable;
  const setShowQeuryMenuTable = useAppStore.getState().setShowQeuryMenuTable;
  const setShowQeuryMenu = useAppStore.getState().setShowQeuryMenu;

  const [isProgress, setIsProgress] = useState(false);
  const [isBbox, setIsBbox] = useState(false);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Example: "2025-09-10"
  };

  // Helper function: format time to HH:mm
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // Example: "14:35"
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // subtract 1 day
    return yesterday.toISOString().split("T")[0];
  };

  // Helper function: get time of one day ago in HH:mm
  const getYesterdayTime = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // subtract 1 day
    return yesterday.toTimeString().slice(0, 5);
  };
  const formatToEpochSec = (date, time) => {
    return Math.floor(new Date(`${date}T${time}:00Z`).getTime() / 1000); // seconds
  };

  const [data, setData] = useState({
    from_date: getYesterdayDate(),
    from_time: getYesterdayTime(),
    to_date: getCurrentDate(),
    to_time: getCurrentTime(),
    device_id: "",
    topics: "",
    recording_id: "",
    city: "",
  });

  const [queryDataSource, setQueryDataSource] = useState(null);

  const getGeojson = async () => {
    setIsProgress(true);
    if (queryDataSource) {
      viewerRef.current.dataSources.remove(queryDataSource, true);
      setQueryDataSource(null);
    }

    try {
      const params = {};

      if (isBbox) {
        params.bbox = getBoundingBox(viewerRef.current);
      }

      if (data.from_date && data.from_time) {
        params.start_time = formatToEpochSec(data.from_date, data.from_time);
      }

      if (data.to_date && data.to_time) {
        params.end_time = formatToEpochSec(data.to_date, data.to_time);
      }

      if (data.device_id) {
        params.device_id = data.device_id;
      }

      if (data.device_name) {
        params.device_name = data.device_name;
      }

      if (data.event_id) {
        params.event_id = data.event_id;
      }

      if (data.topics) {
        params.topics = data.topics;
      }

      if (data.city) {
        params.city = data.city;
      }
      if (data.recording_id) {
        params.recording_id = data.recording_id;
      }

      const response = await fetch("https://matrix.gatik.ai/route_viewer/api/horizon/get-foxglove-recording/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token ea0bb95d88829a5174bae4657242c5c48e121228",
        },
        body: JSON.stringify(params),
      });

      setIsProgress(false);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const geojson = await response.json();

      if (geojson.status == "success") {
        if (geojson.records && geojson.records.length == 0) {
          showToast("Empty result", Type.NOTIFICATION.WARNING);
          return;
        }

        setQeuryMenuTable(geojson.records);
      } else {
        showToast(geojson.message, Type.NOTIFICATION.ERROR);
      }
    } catch (error) {
      console.error("Error loading GeoJSON:", error);
      setIsProgress(false);
    }
  };

  return (
    <Popover.Root positioning={{ placement: "bottom-end" }} size={"lg"} open={showQeuryMenu} onOpenChange={(e) => setShowQeuryMenu(e.open)}>
      <Popover.Trigger asChild>
        <IconButton userSelect={"none"} rounded="full" boxShadow="lg" bg={"Background"}>
          <Tooltip content="Query">
            <LuTextSearch color="MenuText" />
          </Tooltip>
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content width={"fit-content"}>
            <Popover.Arrow />
            <Popover.Body p={2} pt={3}>
              <Text fontSize="md" fontWeight="semibold" mb={1}>
                Query
              </Text>
              <Text fontSize="sm" opacity={0.8} mb={3}>
                Select a time range and optional filters, then fetch matching recordings.
              </Text>

              <Separator mb={3} />

              <HStack gap={2} mb={2}>
                <Field.Root>
                  <Field.Label>From Date </Field.Label>
                  <Input
                    value={data.from_date}
                    type="date"
                    placeholder="From Date"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        from_date: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>From Time </Field.Label>
                  <Input
                    value={data.from_time}
                    type="time"
                    placeholder="From Time"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        from_time: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
              </HStack>
              <HStack gap={2} mb={2}>
                <Field.Root>
                  <Field.Label>To Date </Field.Label>
                  <Input
                    value={data.to_date}
                    type="date"
                    placeholder="To Date"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        to_date: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>To Time </Field.Label>
                  <Input
                    value={data.to_time}
                    type="time"
                    placeholder="To Time"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        to_time: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
              </HStack>

              <HStack gap={2}>
                <Field.Root mb={2}>
                  <Field.Label>Device Id </Field.Label>
                  <Input
                    value={data.device_id}
                    type="text"
                    placeholder="Device Id"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        device_id: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
                <Field.Root mb={2}>
                  <Field.Label>Device Name </Field.Label>
                  <Input
                    value={data.device_name}
                    type="text"
                    placeholder="Device Name"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        device_name: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
              </HStack>

              <HStack gap={2}>
                <Field.Root mb={2}>
                  <Field.Label>Event Id </Field.Label>
                  <Input
                    value={data.event_id}
                    type="text"
                    placeholder="Event Id"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        event_id: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>

                <Field.Root mb={2}>
                  <Field.Label>Topics </Field.Label>
                  <Input
                    value={data.topics}
                    type="text"
                    placeholder="Topics"
                    onChange={(v) => {
                      setData((prev) => ({
                        ...prev,
                        topics: v.target.value,
                      }));
                    }}
                  />
                </Field.Root>
              </HStack>

              <Checkbox.Root
                value={isBbox}
                onCheckedChange={(v) => {
                  setIsBbox(v.checked);
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />

                <Tooltip content="When enabled, results are limited to the area currently visible on the map.">
                  <Checkbox.Label>BBOX</Checkbox.Label>
                </Tooltip>
              </Checkbox.Root>

              <Separator my={3} />

              <HStack gap={2} mt={3}>
                <Button onClick={getGeojson} disabled={isProgress}>
                  {isProgress ? (
                    <Spinner size={"sm"} />
                  ) : (
                    <>
                      <LuSendHorizontal /> Submit
                    </>
                  )}
                </Button>
                {qeuryMenuTable.length > 0 && (
                  <Button
                    onClick={() => {
                      setShowQeuryMenuTable(true);
                      setShowQeuryMenu(false);
                    }}
                  >
                    <LuTable /> Show Result ({qeuryMenuTable.length} records)
                  </Button>
                )}
              </HStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default QueryMenu;
